import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
// eslint-disable-next-line max-len
import { UniversalProductsCategory } from '@1po/1po-bff-fe-spec/generated/catalog/universal_products/response/GetUniversalProductsTreeResponse';
import { useUniversalProductsTree } from 'domains/catalog/Catalog.requests';
import {
  EngineOilLocal,
  UNIVERSAL_PRODUCTS_NODE_ID,
  UNIVERSAL_PRODUCTS_OILS_NODE_ID,
} from 'domains/catalog/Catalog.types';
import {
  getUniversalProductsCatalogLevel2sourcePath,
  getUniversalProductsCatalogLevel3NodeId,
} from 'domains/catalog/Catalog.utils';
import { MaintenanceViewProps } from 'pages/CatalogPage/DH/Maintenance/Maintenance';
import { EngineTable, FluidHeader, SFlex } from 'pages/CatalogPage/DH/Maintenance/Maintenance.styled';
import { DarkHalfCard } from 'pages/CatalogPage/IAM/CarDetailHeader/CarDetailHeader.styled';
import { Box, CenterFlex, Flex, Image, InfoAlert, MarginBox, Pipeline, Space, Text } from 'UI';
import { getData } from 'utils';

const Title = ({ children }: { children: string }) => (
  <Text type="lg" bold disableGutter>
    {children}
  </Text>
);

const getColumns = (
  standards: string[],
  history: ReturnType<typeof useHistory>,
  lvl2tree: UniversalProductsCategory | undefined,
) => {
  const standardsColumns = standards.map((standard) => {
    return {
      title: <Title>{standard}</Title>,
      key: standard,
      render: function renderCheck(row: EngineOilLocal) {
        const capacities = row.dataForTemp.filter((r) => r.standard === standard).map((c) => c.engineCapacity);
        return (
          <CenterFlex>
            {capacities.map((capacity) => (
              <>
                <Text
                  type={'light_14'}
                  displayStyle={'link'}
                  cursor={'pointer'}
                  hoverUnderLine
                  onClick={() => {
                    const lvl3nodeId = getUniversalProductsCatalogLevel3NodeId(lvl2tree, capacity);
                    if (!lvl3nodeId) return;
                    history.push(`${getUniversalProductsCatalogLevel2sourcePath()}/${lvl3nodeId}`);
                  }}
                >
                  {capacity}
                </Text>
                <MarginBox mr={10} />
              </>
            ))}
          </CenterFlex>
        );
      },
    };
  });

  return [
    {
      title: <Title>{'°C'}</Title>,
      dataIndex: 'temp',
      key: 'temp',
      render: function renderTemperature(temperature: string) {
        return <CenterFlex>{`${temperature} °C`}</CenterFlex>;
      },
    },
    ...standardsColumns,
  ];
};

const FluidsSection = ({ maintenancePlanData, getLabel }: MaintenanceViewProps) => {
  const history = useHistory();
  const universalProductsTree = getData(useUniversalProductsTree());
  const lvl2tree = universalProductsTree
    ?.find((l1) => l1.nodeId === UNIVERSAL_PRODUCTS_NODE_ID)
    ?.childCategories?.find((l2) => l2.nodeId === UNIVERSAL_PRODUCTS_OILS_NODE_ID);
  if (
    maintenancePlanData.brakeFluid.length === 0 &&
    maintenancePlanData.coolantFluid.length === 0 &&
    maintenancePlanData.engineOilList.length === 0
  )
    return <></>;
  const hasEngineData = maintenancePlanData.engineOilList.length > 0;

  const getFluidHeader = (title: string) => {
    return (
      <FluidHeader>
        <CenterFlex>
          <Title>{title}</Title>
        </CenterFlex>
      </FluidHeader>
    );
  };

  const FluidInfoCard = ({ title, data }: { title: string; data: string[] }) => {
    if (data.length === 0) return <></>;
    return (
      <>
        {getFluidHeader(title)}
        {data.map((d, index) => {
          return (
            <div key={`${d}_${index}`}>
              <MarginBox my={15} ml={15}>
                <Text type="base" disableGutter>
                  {d}
                </Text>
              </MarginBox>
              <Pipeline size={'100%'} horizontal />
            </div>
          );
        })}
      </>
    );
  };

  const EngineFluids = ({
    engineData,
    standards,
    averageCapacity,
  }: {
    engineData: EngineOilLocal[];
    standards: string[];
    averageCapacity: string[];
  }) => {
    const { t } = useTranslation();

    return (
      <>
        {getFluidHeader(getLabel(30))}
        <Flex>
          <SFlex direction={'column'}>
            <Space size={15} direction={'vertical'}>
              <MarginBox />
              <Text type="base" disableGutter>{`${getLabel(32)}: ${standards.join(', ')}`}</Text>
              <Pipeline size={'100%'} horizontal />
              <Text type="base" bold>
                {getLabel(36)}
              </Text>
              <Text type="base" bold>
                {getLabel(38)}
              </Text>
              <EngineTable
                columns={getColumns(standards, history, lvl2tree)}
                dataSource={engineData}
                pagination={false}
                rowKey={'temp'}
              />
            </Space>
          </SFlex>
          <SFlex direction={'column'}>
            <Space size={15} direction={'vertical'}>
              <MarginBox />
              <Text type="base" disableGutter>{`${t(
                'catalog.maintenance.average_capacity',
                'Average capacity:',
              )} ${averageCapacity.join(', ')}`}</Text>
              <Pipeline size={'100%'} horizontal />
              <Text type="base" bold>
                {getLabel(33)}
              </Text>
              <Image src={'catalog/castrol_logo.png'} alt={'castrol_logo'} />
            </Space>
          </SFlex>
        </Flex>
        <InfoAlert
          size={'lg'}
          message={
            <SFlex direction={'column'}>
              <Text type="base" bold>
                {t('common.note', 'Note')}
              </Text>
              <Box height={10} />
              <Text type="base">{getLabel(39)}</Text>
            </SFlex>
          }
        />
      </>
    );
  };

  return (
    <>
      <DarkHalfCard>
        <Text type={'card_title_dark'}>{getLabel(29)}</Text>
      </DarkHalfCard>
      <Flex direction={'row'}>
        {hasEngineData && (
          <>
            <Flex size={5}>
              <Flex direction={'column'}>
                <EngineFluids
                  engineData={maintenancePlanData.engineOilList}
                  standards={maintenancePlanData.engineOilStandards}
                  averageCapacity={maintenancePlanData.averageCapacity}
                />
              </Flex>
            </Flex>
            <Box width={30} />
          </>
        )}
        <Flex size={4} direction={'column'}>
          <Space size={45} direction={'vertical'}>
            <FluidInfoCard title={getLabel(31)} data={maintenancePlanData.brakeFluid} />
            <FluidInfoCard title={getLabel(37)} data={maintenancePlanData.coolantFluid} />
          </Space>
        </Flex>
        {!hasEngineData && <Flex size={5} />}
      </Flex>
    </>
  );
};

export default FluidsSection;
